import { FormEvent, ReactNode, useId, useState } from 'react';
import { sessionsApi } from '@/service/endpoints/sessions/sessionsApi';
import { settingsButtonClass, settingsInputClass, settingsLabelClass, settingsMessageClass } from './settingsStyles';
import { useTranslation } from 'react-i18next';

type Props = {
  sessionId: string;
  currentName: string;
  /** Called after the name was saved. */
  onRenamed: () => void;
};

const MAX_NAME_LENGTH = 60;

/** Changes the name of the session. */
export const SessionNameForm = ({ sessionId, currentName, onRenamed }: Props): ReactNode => {
  const { t } = useTranslation();
  const inputId = useId();
  const [name, setName] = useState(currentName);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const trimmed = name.trim();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    try {
      await sessionsApi.renameSession(sessionId, trimmed);
      setMessage(t('nameForm.saved'));
      onRenamed();
    } catch (error) {
      setMessage((error as { message?: string })?.message || t('common.somethingWentWrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <label htmlFor={inputId} className={settingsLabelClass}>
        {t('nameForm.label')}
      </label>
      <div className="flex gap-2">
        <input
          id={inputId}
          className={settingsInputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={MAX_NAME_LENGTH}
        />
        <button
          type="submit"
          disabled={busy || !trimmed || trimmed === currentName}
          className={settingsButtonClass}
        >
          {t('nameForm.save')}
        </button>
      </div>
      {message && <p className={`mt-2 ${settingsMessageClass}`}>{message}</p>}
    </form>
  );
};
